import React, { useState, useEffect } from 'react';
import 'leaflet/dist/leaflet.css';
import { useNavigate, useParams } from 'react-router-dom';
import MapComponent from '../Maps/MapComponent';
import EditIcon from "../../assets/icons/edit.svg";
import ShareIcon from "../../assets/icons/share.svg";
import DeleteIcon from "../../assets/icons/delete.svg";
import CheckIcon from "../../assets/icons/checked.svg";
import CopyIcon from "../../assets/icons/copy.svg";
import { EmailShareButton, WhatsappShareButton, FacebookShareButton, XShareButton } from "react-share";
import { EmailIcon, WhatsappIcon, FacebookIcon, TwitterIcon, XIcon } from "react-share";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import MapIcon from '../../assets/icons/Maps.svg';
import GridIcon from '../../assets/icons/Grid.svg';
import OfficeIcon from '../../assets/icons/OfficeList.svg';
import OtherIcon from '../../assets/icons/OtherList.svg';
import HomeIcon from '../../assets/icons/HomeLIst.svg';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import { getUserProfile, UserProfile } from '../../API/UserProfileApi/GetUserProfile';
import { Address, viewAllAddedAddressByUserAsync } from '../../API/Address/ViewAllAddedAddressByUserAsync';
import { requestDeleteAddressAsync } from '../../API/Address/RequestDeleteAddressAsync.ts';
import FilterIcon from '../../assets/icons/FilterCircle.svg';
import ReactGA from "react-ga4";
import { useLocalization } from "../Context/MultiLanguageContext";
import LocationIcon from '../../assets/icons/Newmap.svg';
import FilterPanel from './FilterPanel';
import DeleteModal from './DeleteModal';
import { getUserIdFromToken } from '../../auth/authUtils';
import { ShareLink } from '../../API/Address/SharePost';
import { getApiBaseUrl, PREPROD_API_BASE_URL } from '../../utils/apiBaseUrl';

const AddressList: React.FC<{ updateUserName: (newUserName: string) => void }> = ({ updateUserName }) => {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { id } = useParams<{ id: string }>();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [addresses, setAddresses] = useState<any[]>([]);
  const [viewAsGrid, setViewAsGrid] = useState(false);
  const [addressResults, setAddressResults] = useState<Address[]>([]);
  const [copiedField, setCopiedField] = useState<{ type: "pamCode" | "digiPin"; value: string } | null>(null);
  const [searchData, setSearchData] = useState("");
  const [address, setAddress] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [addressToDelete, setAddressToDelete] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [activeSharePamCode, setActiveSharePamCode] = useState<string | null>(null);
  const [userName, setUserName] = useState('');
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [noAddressFound, setNoAddressFound] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  const [selectedTypes, setSelectedTypes] = useState<string[]>(() => {
    const stored = localStorage.getItem("selectedTypes");
    return stored ? JSON.parse(stored) : [];
  });
  const [appliedSelectedTypes, setAppliedSelectedTypes] = useState<string[]>(() => {
    const stored = localStorage.getItem("appliedSelectedTypes");
    return stored ? JSON.parse(stored) : [];
  });
  const [filterSearch, setFilterSearch] = useState<string>(
    localStorage.getItem('filterSearch') || ''
  );
  const [appliedFilterSearch, setAppliedFilterSearch] = useState<string>(() => {
    return localStorage.getItem("appliedFilterSearch") || "";
  });

  const [sortOrder, setSortOrder] = useState<string>("az");
  const [appliedSortOrder, setAppliedSortOrder] = useState<string>("az");

  type ShareData = {
    url: string;
    referralLink?: string;
  };

  const [shareUrls, setShareUrls] = useState<{ [key: string]: ShareData }>({});

  const [isLoadingShare, setIsLoadingShare] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    localStorage.setItem("selectedTypes", JSON.stringify(selectedTypes));
  }, [selectedTypes]);

  useEffect(() => {
    localStorage.setItem("filterSearch", filterSearch);
  }, [filterSearch]);

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: window.location.pathname + window.location.search,
    });
  }, []);

  useEffect(() => {
    const userIdFromToken = getUserIdFromToken();
    setUserId(userIdFromToken);
  }, []);

  const getFilteredResults = (search: string): Address[] => {
    let filtered = addresses;

    if (appliedSelectedTypes.length > 0) {
      filtered = filtered.filter(addr => {
        const type = addr.addressType.toLowerCase();
        const includeHome = appliedSelectedTypes.includes('home') && type === 'home';
        const includeOffice = appliedSelectedTypes.includes('office') && type === 'office';
        const includeOther = appliedSelectedTypes.includes('other') && type !== 'home' && type !== 'office';
        return includeHome || includeOffice || includeOther;
      });
    }

    const inputValue = search.trim();
    const sanitizedInput = inputValue.replace(/-/g, '');
    if (sanitizedInput !== '') {
      if (parseInt(sanitizedInput).toString() === sanitizedInput) {
        filtered = filtered.filter(addr => {
          if (sanitizedInput.length === 6) {
            return addr.pinCode && addr.pinCode.includes(sanitizedInput);
          } else if (sanitizedInput.length === 4) {
            return addr.blockCode && addr.blockCode.includes(sanitizedInput);
          } else if (sanitizedInput.length > 10) {
            return addr.pamCode && addr.pamCode.includes(sanitizedInput);
          } else if (sanitizedInput.length < 4) {
            return addr.pamCode && addr.pamCode.includes(sanitizedInput);
          } else if (sanitizedInput.length === 10) {
            return addr.pamCode && sanitizedInput.includes(addr.pamCode.slice(0, 10));
          } else {
            return addr.pamCode && addr.pamCode.includes(sanitizedInput);
          }
        });
      } else {
        filtered = filtered.filter(addr =>
          addr.addressLine1.toLowerCase().includes(sanitizedInput.toLowerCase()) ||
          addr.addressLine2.toLowerCase().includes(sanitizedInput.toLowerCase()) ||
          addr.addressLine3.toLowerCase().includes(sanitizedInput.toLowerCase()) ||
          addr.addressType.toLowerCase().includes(sanitizedInput.toLowerCase())
        );
      }
    }
    return filtered;
  };

  const sortAddresses = (data: Address[], order: string) => {
    return [...data].sort((a, b) => {
      const aName = (a.addressLine1 || "").trim().toLowerCase();
      const bName = (b.addressLine1 || "").trim().toLowerCase();

      return order === "az"
        ? aName.localeCompare(bName)
        : bName.localeCompare(aName);
    });
  };

  const handleCheckboxChange = (type: string) => {
    if (selectedTypes.includes(type)) {
      setSelectedTypes(selectedTypes.filter(t => t !== type));
    } else {
      setSelectedTypes([...selectedTypes, type]);
    }
  };

  const handleApply = () => {
    setAppliedSelectedTypes(selectedTypes);
    setAppliedFilterSearch(filterSearch);
    setAppliedSortOrder(sortOrder); // <-- ADD THIS

    localStorage.setItem(
      "appliedSelectedTypes",
      JSON.stringify(selectedTypes)
    );
    localStorage.setItem("appliedFilterSearch", filterSearch);

    let filteredResults = getFilteredResults(filterSearch);

    filteredResults = sortAddresses(filteredResults, sortOrder);

    if (filteredResults.length === 0) {
      setNoAddressFound(true);
      setShowFilterPanel(true);
    } else {
      setNoAddressFound(false);
      setAddressResults(filteredResults);
      setShowFilterPanel(false);
    }
  };

  const resetFilter = () => {
    setSelectedTypes([]);
    setAppliedSelectedTypes([]);

    setFilterSearch("");
    setAppliedFilterSearch("");

    setSortOrder("az");
    setAppliedSortOrder("az");

    localStorage.removeItem("selectedTypes");
    localStorage.removeItem("appliedSelectedTypes");
    localStorage.removeItem("filterSearch");
    localStorage.removeItem("appliedFilterSearch");

    const sortedAddresses = sortAddresses(addresses, "az");

    setAddressResults(sortedAddresses);
    setNoAddressFound(sortedAddresses.length === 0);
    setShowFilterPanel(false);
  };

  useEffect(() => {
    if (addresses.length > 0) {
      if (
        appliedSelectedTypes.length === 0 &&
        appliedFilterSearch.trim() === ""
      ) {
        const sorted = sortAddresses(addresses, appliedSortOrder);

        setAddressResults(sorted);
        setNoAddressFound(false);
      } else {
        const filtered = getFilteredResults(appliedFilterSearch);
        const sorted = sortAddresses(filtered, appliedSortOrder);

        setAddressResults(sorted);
        setNoAddressFound(sorted.length === 0);
      }
    } else {
      setAddressResults([]);
      setNoAddressFound(false);
    }
  }, [
    addresses,
    appliedSelectedTypes,
    appliedFilterSearch,
    appliedSortOrder,
  ]);

  useEffect(() => {
    setIsFormChanged(deleteReason.trim().length > 0);
  }, [deleteReason]);

  const handleAddressClick = (address: Address) => {
    ReactGA.event({
      category: "AddressList",
      action: "Address Clicked",
      label: address.pamCode || address.id,
    });
    navigate(`/address-details/${address.id}`, { state: { address } });
  };

  const handleCopy = async (value: string, type: "pamCode" | "digiPin") => {
    try {
      const finalValue = String(value ?? "").trim();
      await navigator.clipboard.writeText(finalValue);
      setCopiedField({ type, value: finalValue });
      setTimeout(() => setCopiedField(null), 2000);
      ReactGA.event({
        category: "AddressList",
        action: `${type === "pamCode" ? "PAM Code" : "DigiPin"} Copied`,
        label: finalValue,
      });
    } catch (err) {
    }
  };

  const handleEdit = (address: Address) => {
    ReactGA.event({
      category: "AddressList",
      action: "Edit Address Clicked",
      label: address.id,
    });
    navigate(`/edit-address/${address.id}`, { state: { address } });
  };

  useEffect(() => {
    const storedUserName = localStorage.getItem('userName');
    if (storedUserName) {
      setUserName(storedUserName);
    }
  }, []);

  const fetchAndHandleUserProfile = async () => {
    try {
      const data = await getUserProfile(t);
      setUserProfile(data);
      setUserName(data.userName);
      updateUserName(data.userName);
      ReactGA.event({
        category: "AddressList",
        action: "User Profile Fetched",
        label: data.userName,
      });
      navigate('/profile', {
        state: {
          userName: data.userName,
          email: data.email,
          phoneNumber: data.phoneNumber,
        },
      });
    } catch (error) {
      toast.error(t("sessionExpired"));
      navigate('/home');
    }
  };

  const handleShareClick = async (pamCode: string, addressId: string) => {
    ReactGA.event({
      category: "AddressList",
      action: "Share Clicked",
      label: pamCode,
    });

    if (activeSharePamCode === pamCode) {
      setActiveSharePamCode(null);
      return;
    }

    setIsLoadingShare(prev => ({ ...prev, [addressId]: true }));

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error(t("sessionExpired"));
        navigate('/');
        return;
      }

      const shareResponse = await ShareLink(parseInt(addressId), token);

      if (shareResponse) {
        setShareUrls(prev => ({
          ...prev,
          [addressId]: {
            url: shareResponse.url,
            referralLink: shareResponse.referralLink
          }
        }));

        setActiveSharePamCode(pamCode);
      }
    } catch (error) {
      console.error('Error calling share API:', error);
      toast.error(t("errorGeneratingShareLink"));
    } finally {
      setIsLoadingShare(prev => ({ ...prev, [addressId]: false }));
    }
  };

  const formatPamCode = (pamCode: string) => {
    if (!pamCode) return "N/A";

    if (pamCode.includes("-")) return pamCode;

    const pincode = pamCode.slice(0, 6);
    const blockCode = pamCode.slice(6, 10);
    const addressCode = pamCode.slice(10);

    if (addressCode) return `${pincode}-${blockCode}-${addressCode}`;
    return `${pincode}-${blockCode}`;
  };

  const buildFormattedPamCode = (address: any) => {
    const pin = String(address?.pinCode ?? "").trim();
    const block = String(address?.blockCode ?? "").trim();
    const addr = String(address?.addressCode ?? "").trim();

    if (!pin || !block || !addr) {
      return formatPamCode(address?.pamCode || "");
    }

    return `${pin}-${block}-${addr}`;
  };

  const shareMessage = (address: Address) => {
    const isPreprod =
      process.env.REACT_APP_API_BASE_URL === "https://preprod-api.getmethere.in";

    const shareData = shareUrls[address.id];
    const dynamicUrl = shareData?.url;
    const referralLink = shareData?.referralLink;

    const fallbackUrl = isPreprod
      ? `https://preprod-app.getmethere.in/${buildFormattedPamCode(address)}`
      : `https://app.getmethere.in/${buildFormattedPamCode(address)}`;

    const addressLink = dynamicUrl || fallbackUrl;
    const firstName = (userName || "").trim().split(/\s+/)[0] || "";

    const userRefLink = referralLink
      ? referralLink
      : (isPreprod
        ? `https://preprod.getmethere.in/?ref=${userId || ""}&by=${firstName}`
        : `https://getmethere.in/?ref=${userId || ""}&by=${firstName}`);

    return `Check out this address:
${address?.addressLine1}, ${address?.addressLine2}, ${address?.addressLine3}
PAM Code: ${buildFormattedPamCode(address)}
Digi Pin: ${address?.digiPin || "N/A"}
Address Link: ${addressLink}

More details: ${userRefLink}`;
  };

  const handleDelete = (addressId: string) => {
    setShowDeleteModal(true);
    setAddressToDelete(addressId);
    setDeleteReason('');
  };

  const handleDeleteSubmit = async () => {
    const id = addressToDelete;
    if (!id) {
      toast.error(t("addressIdMissing"));
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error(t("sessionExpired"));
      navigate('/');
      return;
    }

    try {
      await requestDeleteAddressAsync(parseInt(id), deleteReason, token);

      setAddresses((prevAddresses) =>
        prevAddresses.map((address) =>
          address.id === id ? { ...address, isDeleteRequestSubmitted: true } : address
        )
      );

      setShowDeleteModal(false);
      setDeleteReason('');
      setAddressToDelete('');
      ReactGA.event({
        category: "AddressList",
        action: "Delete Request Submitted",
        label: id,
      });
      setTimeout(() => {
        navigate("/addresslist");
      }, 3000);
    } catch (error) {
    }
  };

  const handleLocationIconClick = (latitude: number, longitude: number) => {
    ReactGA.event({
      category: "Favourites",
      action: "Clicked Location Icon",
      label: `Lat: ${latitude}, Lng: ${longitude}`,
    });
    const defaultMap = localStorage.getItem("defaultMap") || "google";

    if (defaultMap === "waze") {
      const wazeMapsUrl = `https://waze.com/ul?ll=${latitude},${longitude}&navigate=yes`;
      window.open(wazeMapsUrl, '_blank');
    } else if (defaultMap === "mapindia") {
      const mapplsUrl = `https://www.mappls.com/@${latitude},${longitude}`;
      window.open(mapplsUrl, "_blank");
    } else if (defaultMap === "apple") {
      const appleMapsUrl = `https://maps.apple.com/?ll=${latitude},${longitude}&q=${latitude},${longitude}&z=18&t=m`;
      window.open(appleMapsUrl, "_blank");
    } else {
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
      window.open(googleMapsUrl, '_blank');
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteReason('');
    setAddressToDelete('');
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const fetchAddresses = async () => {
    setIsLoading(true);
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error(t("sessionExpired"));
      navigate('/');
      return;
    }
    try {
      const data: any[] | null = await viewAllAddedAddressByUserAsync(token, t);
      if (data === null) {
        setIsLoading(false);
        return;
      }
      const sortedAddresses = [...data].sort((a, b) =>
        (a.addressLine1 || "").localeCompare(b.addressLine1 || "", undefined, {
          sensitivity: "base",
        })
      );

      setAddresses(sortedAddresses);

      if (appliedSelectedTypes.length === 0 && filterSearch.trim() === "") {
        setAddressResults(sortedAddresses);
      }
      ReactGA.event({
        category: "AddressList",
        action: "Addresses Fetched",
        label: `Total: ${data.length}`,
      });
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const activeFilterCount =
    appliedSelectedTypes.length + (appliedFilterSearch.trim() !== "" ? 1 : 0);

  const handleLogout = () => {
    localStorage.removeItem('userToken');
    localStorage.removeItem('authToken');
    localStorage.removeItem('token');
    localStorage.removeItem('userName');

    sessionStorage.clear();
    localStorage.removeItem('filterType');
    localStorage.removeItem('filterSearch');
    localStorage.removeItem('appliedFilterType');
    localStorage.removeItem('appliedFilterSearch');
    ReactGA.event({
      category: "AddressList",
      action: "Logout",
    });
    navigate("/", { replace: true });
  };

  useEffect(() => {
    fetchAddresses();
  }, []);

  const initialPosition = addressResults.length > 0
    ? [addressResults[0].latitude, addressResults[0].longitude]
    : [40.7128, -74.0060];

  const AddressSkeleton: React.FC = () => {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-lg bg-white shadow-shadow1 w-auto p-4 mb-14">
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index} className="p-4 border rounded-md animate-pulse">
            <div className="h-4 w-1/2 bg-gray-300 mb-2"></div>
            <div className="h-4 w-full bg-gray-300 mb-2"></div>
            <div className="h-4 w-3/4 bg-gray-300 mb-2"></div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-slate-100 flex flex-col justify-between min-h-screen relative">
      <ToastContainer />
      <Header userName={userName} fetchUserProfile={fetchAndHandleUserProfile} handleLogout={handleLogout} />
      <main className="relative flex-1 mt-6 px-4 space-y-4 mb-8">
        <div className="mb-4">
          <div className="flex justify-between items-center mb-4 sm:mb-0">
            <div className="flex items-center gap-4">
              <h1 className="text-base sm:text-lg text-dark font-semibold">
                {t("addressList")}
              </h1>
              <div className="hidden sm:block rounded-lg shadow-shadow1 border bg-white border-white">
                <ul
                  className="relative flex flex-wrap px-2.5 py-3.5 list-none rounded-md bg-white"
                  data-tabs="tabs"
                  role="list"
                >
                  <li className="flex-auto text-center">
                    <a
                      className={`flex items-center justify-center w-full px-4 py-2 text-sm mb-0 transition-all ease-in-out border border-white rounded-md cursor-pointer ${!viewAsGrid
                        ? 'border-white text-blue-500 bg-[#D6DFFE]'
                        : 'text-slate-600 bg-inherit'
                        }`}
                      role="tab"
                      aria-selected={viewAsGrid}
                      onClick={() => setViewAsGrid(false)}
                    >
                      <img
                        src={GridIcon}
                        alt="View as Grid"
                        className="w-4 h-4 mr-1.5"
                      />
                      <span className="ml-1">
                        {t("details")}
                      </span>
                    </a>
                  </li>

                  <li className="flex-auto text-center mr-2">
                    <a
                      className={`flex items-center justify-center w-full px-4 py-2 text-sm mb-0 transition-all ease-in-out border border-white rounded-md cursor-pointer ${viewAsGrid
                        ? 'border-white text-blue-500 bg-[#D6DFFE]'
                        : 'text-slate-600 bg-inherit'
                        }`}
                      role="tab"
                      aria-selected={!viewAsGrid}
                      onClick={() => setViewAsGrid(true)}
                    >
                      <img
                        src={MapIcon}
                        alt="View on Map"
                        className="w-4 h-4 mr-1.5"
                      />
                      <span className="ml-1">
                        {t("map")}
                      </span>
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            <div className="relative">
              <button
                onClick={() => setShowFilterPanel(!showFilterPanel)}
                className="flex items-center px-4 py-2 border border-blue-500 bg-white text-blue-500 rounded-md shadow-md hover:bg-blue-500 hover:text-white transition-colors duration-300 transform hover:scale-105 relative"
              >
                <img src={FilterIcon} alt="Filter" className="w-4 h-4 sm:w-6 sm:h-6 mr-1" />
                <span>{addressResults.length} {t("items")}</span>
              </button>
              {activeFilterCount > 0 && (
                <span className="absolute -top-2 -right-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </div>
          </div>
          <div className="sm:hidden">
            <div className="rounded-lg shadow-shadow1 w-full border bg-white border-white">
              <ul
                className="relative flex flex-wrap px-2.5 py-3.5 list-none rounded-md bg-white"
                data-tabs="tabs"
                role="list"
              >
                <li className="flex-auto text-center">
                  <a
                    className={`flex items-center justify-center w-full px-4 py-2 text-sm mb-0 transition-all ease-in-out border border-white rounded-md cursor-pointer ${!viewAsGrid
                      ? 'border-white text-blue-500 bg-[#D6DFFE]'
                      : 'text-slate-600 bg-inherit'
                      }`}
                    role="tab"
                    aria-selected={viewAsGrid}
                    onClick={() => setViewAsGrid(false)}
                  >
                    <img
                      src={GridIcon}
                      alt="View as Grid"
                      className="w-4 h-4 mr-1.5"
                    />
                    <span className="ml-1">
                      {t("details")}
                    </span>
                  </a>
                </li>

                <li className="flex-auto text-center mr-2">
                  <a
                    className={`flex items-center justify-center w-full px-4 py-2 text-sm mb-0 transition-all ease-in-out border border-white rounded-md cursor-pointer ${viewAsGrid
                      ? 'border-white text-blue-500 bg-[#D6DFFE]'
                      : 'text-slate-600 bg-inherit'
                      }`}
                    role="tab"
                    aria-selected={!viewAsGrid}
                    onClick={() => setViewAsGrid(true)}
                  >
                    <img
                      src={MapIcon}
                      alt="View on Map"
                      className="w-4 h-4 mr-1.5"
                    />
                    <span className="ml-1">
                      {t("map")}
                    </span>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {showFilterPanel && (
          <FilterPanel
            selectedTypes={selectedTypes}
            handleCheckboxChange={handleCheckboxChange}
            filterSearch={filterSearch}
            setFilterSearch={setFilterSearch}
            noAddressFound={noAddressFound}
            t={t}
            resetFilter={resetFilter}
            handleApply={handleApply}
            setShowFilterPanel={setShowFilterPanel}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
          />
        )}

        {errorMessage && <div className="text-red-500">{errorMessage}</div>}
        {successMessage && <div className="text-green-500">{successMessage}</div>}
        {isLoading ? (
          <AddressSkeleton />
        ) : !viewAsGrid ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 rounded-lg bg-white shadow-shadow1 w-full p-2 sm:p-4 mb-14">
            {addressResults.length > 0 ? (
              addressResults.map((address, index) => {
                const formattedPamCode = buildFormattedPamCode(address);

                return (
                  <div
                    key={address.id || index}
                    className="relative border p-2 sm:p-3 rounded-md shadow-sm cursor-pointer hover:border-blue-500"
                    onClick={(e) => {
                      const selectedText = window.getSelection()?.toString();

                      if (selectedText) return;

                      const target = e.target as HTMLElement;

                      if (!target.closest('button')) {
                        handleAddressClick(address);
                      }
                    }}
                  >
                    <div className='flex justify-between items-start mb-3 sm:grid sm:grid-cols-2 sm:gap-3'>
                      <div className="text-sm sm:order-1 order-1 sm:text-base font-medium text-gray-600 flex items-center">
                        {address.addressType.toLowerCase() === 'office' && (
                          <>
                            <img src={OfficeIcon} alt="Office Icon" className="w-6 h-6 sm:w-8 sm:h-8 mr-2" />
                            <span className="text-xs sm:text-base">{t("office")}</span>
                          </>
                        )}
                        {address.addressType.toLowerCase() === 'home' && (
                          <>
                            <img src={HomeIcon} alt="Home Icon" className="w-6 h-6 sm:w-8 sm:h-8 mr-2" />
                            <span className="text-xs sm:text-base">{t("home")}</span>
                          </>
                        )}
                        {address.addressType.toLowerCase() === 'other' && (
                          <>
                            <img src={OtherIcon} alt="Other Icon" className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                            <span className="text-xs sm:text-base">{t("other")}</span>
                          </>
                        )}
                        {!['office', 'home', 'other'].includes(address.addressType.toLowerCase()) && (
                          <>
                            <img src={OtherIcon} alt="Custom Type Icon" className="w-6 h-6 sm:w-8 sm:h-8 mr-2" />
                            <span className="text-xs sm:text-base">{address.addressType}</span>
                          </>
                        )}
                      </div>

                      <div className="flex gap-1 sm:gap-2 sm:grid sm:grid-cols-3 sm:order-2 order-2 items-start sm:justify-end">
                        <div className="flex gap-1 sm:gap-2 col-span-3 order-2 justify-end flex-wrap">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleShareClick(address.pamCode, address.id); }}
                            title="Share"
                            disabled={isLoadingShare[address.id]}
                            className={`p-1 sm:p-1.5 border border-[#D6DFFE] bg-blue-50 rounded-md hover:bg-[#c4d4fc] focus:outline-none flex-shrink-0 ${isLoadingShare[address.id] ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            {isLoadingShare[address.id] ? (
                              <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <img src={ShareIcon} alt="Share" className="w-3 h-3 sm:w-4 sm:h-4" />
                            )}
                          </button>
                          {activeSharePamCode === address.pamCode && (
                            <div className="absolute top-8 right-0 z-20 p-2 sm:p-4 bg-white shadow-lg rounded-lg border border-gray-200 flex gap-2 sm:gap-4 mt-2 transform scale-90 sm:scale-105">
                              <EmailShareButton url={shareMessage(address)} className="transform transition-transform duration-300 hover:scale-125 hover:shadow-xl">
                                <EmailIcon size={24} round className="sm:w-8 sm:h-8" />
                              </EmailShareButton>
                              <WhatsappShareButton url={shareMessage(address)} className="transform transition-transform duration-300 hover:scale-125 hover:shadow-xl">
                                <WhatsappIcon size={24} round className="sm:w-8 sm:h-8" />
                              </WhatsappShareButton>
                              <FacebookShareButton url={shareMessage(address)} className="transform transition-transform duration-300 hover:scale-125 hover:shadow-xl">
                                <FacebookIcon size={24} round className="sm:w-8 sm:h-8" />
                              </FacebookShareButton>
                              <XShareButton url={shareMessage(address)} className="transform transition-transform duration-300 hover:scale-125 hover:shadow-xl">
                                <XIcon size={24} round className="sm:w-8 sm:h-8" />
                              </XShareButton>
                            </div>
                          )}
                          <button
                            title="Location"
                            className="p-1 sm:p-1.5 border border-[#D6DFFE] bg-gray-100 rounded-md hover:bg-[#c4d4fc] focus:outline-none flex-shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLocationIconClick(address.latitude, address.longitude);
                            }}
                          >
                            <img src={LocationIcon} alt="Location" className="h-3 w-3 sm:h-4 sm:w-4 cursor-pointer" />
                          </button>

                          <button onClick={(e) => { e.stopPropagation(); handleEdit(address); }} title="Edit" className="p-1 sm:p-1.5 border border-[#D6DFFE] bg-green-50 rounded-md hover:bg-[#c4d4fc] focus:outline-none flex-shrink-0">
                            <img src={EditIcon} alt="Edit" className="w-3 h-3 sm:w-4 sm:h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (address.isDeleteRequestSubmitted) {
                                if (!toast.isActive('delete-request')) {
                                  toast.info(t("deleteRequestSubmitted"), { toastId: 'delete-request' });
                                }
                              } else {
                                handleDelete(address.id);
                              }
                            }}
                            title="Delete"
                            className="p-1 sm:p-1.5 border border-[#D6DFFE] bg-red-50 rounded-md hover:bg-[#c4d4fc] focus:outline-none flex-shrink-0"
                          >
                            <img src={DeleteIcon} alt="Delete" className="w-3 h-3 sm:w-4 sm:h-4" />
                          </button>
                        </div>
                      </div>
                    </div>

                    <p
                      className="text-xs sm:text-sm font-semibold text-gray-700 mt-2 break-words select-text"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {address.addressLine1}
                    </p>

                    <p
                      className="text-xs sm:text-sm text-[#61677D] break-words mb-2 select-text"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {address.addressLine2}, {address.addressLine3}, {address.postOfficeName} P.O.
                    </p>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-2 w-full mb-2 mt-3 sm:mt-0">
                      <label htmlFor="pamCode" className="text-xs sm:text-sm font-medium whitespace-nowrap w-full sm:w-24">
                        {t("pamCode")}:
                      </label>
                      <div className="relative flex-1">
                        <input
                          type="text"
                          className="w-full p-1.5 sm:p-2 pr-8 sm:pr-10 border rounded-md text-xs sm:text-sm"
                          value={formattedPamCode}
                          readOnly
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                        />
                        <button
                          className="absolute right-1 sm:right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-blue-500"
                          onClick={() => handleCopy(formattedPamCode, "pamCode")}
                        >
                          {copiedField?.type === "pamCode" && copiedField.value === formattedPamCode ? (
                            <img src={CheckIcon} alt="Copied" className="w-3 h-3 sm:w-4 sm:h-4" />
                          ) : (
                            <img src={CopyIcon} alt="Copy" className="w-3 h-3 sm:w-4 sm:h-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-2 w-full mb-2 mt-2">
                      <label htmlFor="digipin" className="text-xs sm:text-sm font-medium whitespace-nowrap w-full sm:w-24">
                        {t("digiPin")}:
                      </label>
                      <div className="relative flex-1">
                        <input type="text" className="w-full p-1.5 sm:p-2 pr-8 sm:pr-10 border rounded-md text-xs sm:text-sm"
                          value={address.digiPin}
                          readOnly
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()} />

                        <button className="absolute right-1 sm:right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-blue-500" onClick={() => handleCopy(address.digiPin, "digiPin")}>
                          {copiedField?.type === "digiPin" && copiedField.value === address.digiPin ? (
                            <img src={CheckIcon} alt="Copied" className="w-3 h-3 sm:w-4 sm:h-4" />
                          ) : (
                            <img src={CopyIcon} alt="Copy" className="w-3 h-3 sm:w-4 sm:h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-gray-500">{t("noAddresses")}</p>
            )}
          </div>
        ) : (
          <div className="p-3 mx-auto bg-white m-0 rounded-lg h-500px w-[100%]">
            <div className="w-full overflow-hidden" style={{ height: 'calc(100vh - (300px))' }}>
              <MapComponent addresses={addressResults} showLocateControl={false} />
            </div>
          </div>
        )}

        <DeleteModal
          isOpen={showDeleteModal}
          addressId={addressToDelete}
          t={t}
          onClose={() => {
            setShowDeleteModal(false);
            setDeleteReason("");
            setAddressToDelete("");
          }}
          navigate={navigate}
          setErrorMessage={setErrorMessage}
          setSuccessMessage={setSuccessMessage}
          setAddresses={setAddresses}
        />
      </main>
    </div>
  );
};

export default AddressList;